import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Paper,
  Stack,
  Drawer,
  Divider,
  Tooltip,
  Collapse,
  alpha,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRowParams } from '@mui/x-data-grid';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventsService } from '../services/eventsService';
import { blacklistService } from '../services/blacklistService';
import { EnrichedEvent } from '../types/api';
import { format, formatDistanceToNow } from 'date-fns';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import PublicIcon from '@mui/icons-material/Public';
import DnsIcon from '@mui/icons-material/Dns';
import RouterIcon from '@mui/icons-material/Router';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanIcon from '@mui/icons-material/Lan';
import StorageIcon from '@mui/icons-material/Storage';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';
import DataObjectIcon from '@mui/icons-material/DataObject';
import SensorsIcon from '@mui/icons-material/Sensors';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// ============================================================
// Helper Components
// ============================================================

const DetailRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  copyable?: string;
}> = ({ icon, label, value, mono, copyable }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (copyable) {
      navigator.clipboard.writeText(copyable);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
      <Box sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: mono ? '"JetBrains Mono", "Fira Code", monospace' : 'inherit',
              fontWeight: mono ? 500 : 400,
              wordBreak: 'break-all',
            }}
          >
            {value || <span style={{ color: '#94a3b8' }}>-</span>}
          </Typography>
          {copyable && (
            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
              <IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5, opacity: 0.5, '&:hover': { opacity: 1 } }}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const SectionHeader: React.FC<{ title: string; icon: React.ReactNode }> = ({ title, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 2.5 }}>
    <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
      {title}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', background: (theme) => alpha(theme.palette.divider, 0.5), ml: 1 }} />
  </Box>
);

// ============================================================
// Quick Filter Presets
// ============================================================

type TimePreset = '1h' | '6h' | '24h' | '7d' | '30d' | 'custom';

const timePresets: { label: string; value: TimePreset }[] = [
  { label: '1h', value: '1h' },
  { label: '6h', value: '6h' },
  { label: '24h', value: '24h' },
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
];

function getPresetDate(preset: TimePreset): string | undefined {
  if (preset === 'custom') return undefined;
  const now = new Date();
  const map: Record<string, number> = { '1h': 1, '6h': 6, '24h': 24, '7d': 168, '30d': 720 };
  now.setHours(now.getHours() - (map[preset] || 720));
  return now.toISOString();
}

// ============================================================
// Protocol & Action color mapping
// ============================================================

function getProtocolColor(proto?: string): 'primary' | 'secondary' | 'warning' | 'info' | 'default' {
  if (!proto) return 'default';
  const p = proto.toLowerCase();
  if (p === 'tcp') return 'primary';
  if (p === 'udp') return 'warning';
  if (p === 'icmp') return 'info';
  return 'default';
}

function getActionColor(action?: string): 'error' | 'success' | 'warning' | 'default' {
  if (!action) return 'default';
  const a = action.toLowerCase();
  if (a === 'denied' || a === 'drop' || a === 'blocked') return 'error';
  if (a === 'allowed' || a === 'accept') return 'success';
  return 'warning';
}

function getActionIcon(action?: string) {
  if (!action) return null;
  const a = action.toLowerCase();
  if (a === 'denied' || a === 'drop' || a === 'blocked') return <WarningAmberIcon sx={{ fontSize: 14 }} />;
  if (a === 'allowed' || a === 'accept') return <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />;
  return null;
}

// ============================================================
// Country flag emoji helper
// ============================================================

function countryFlag(code?: string): string {
  if (!code || code.length !== 2) return '';
  const codePoints = code.toUpperCase().split('').map((c) => 0x1f1e6 + c.charCodeAt(0) - 65);
  return String.fromCodePoint(...codePoints);
}

// ============================================================
// Main Component
// ============================================================

export const EventsPage: React.FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  // Force re-render every minute to update relative times
  const [, setCurrentTime] = useState(Date.now());
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  // Pagination
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  // Filters
  const [timePreset, setTimePreset] = useState<TimePreset>('24h');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    sourceIp: '',
    countryCode: '',
    protocol: '',
    asn: '',
    startDate: '',
    endDate: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({ ...filters });

  // Detail drawer
  const [selectedEvent, setSelectedEvent] = useState<EnrichedEvent | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [metadataExpanded, setMetadataExpanded] = useState(false);

  // Quick search (IP)
  const [quickSearch, setQuickSearch] = useState('');

  // Computed from date
  const fromDate = useMemo(() => {
    if (timePreset === 'custom' && appliedFilters.startDate) return appliedFilters.startDate;
    return getPresetDate(timePreset);
  }, [timePreset, appliedFilters.startDate]);

  // Active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.sourceIp) count++;
    if (appliedFilters.countryCode) count++;
    if (appliedFilters.protocol) count++;
    if (appliedFilters.asn) count++;
    if (appliedFilters.startDate && timePreset === 'custom') count++;
    if (appliedFilters.endDate && timePreset === 'custom') count++;
    return count;
  }, [appliedFilters, timePreset]);

  // API query
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['events', paginationModel.page + 1, paginationModel.pageSize, fromDate, appliedFilters, quickSearch],
    queryFn: () =>
      eventsService.getAll({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        sourceIp: quickSearch || appliedFilters.sourceIp || undefined,
        country: appliedFilters.countryCode || undefined,
        protocol: appliedFilters.protocol || undefined,
        asn: appliedFilters.asn ? parseInt(appliedFilters.asn) : undefined,
        from: fromDate,
        to: timePreset === 'custom' && appliedFilters.endDate ? appliedFilters.endDate : undefined,
      }),
    refetchInterval: 30000,
    staleTime: 5000,
  });

  // Blacklist mutation
  const blacklistMutation = useMutation({
    mutationFn: (ipAddress: string) =>
      blacklistService.addIp({ ipAddress, reason: 'manual', notes: 'Added from events viewer' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
    },
  });

  const handleBlacklistIp = useCallback((ipAddress: string) => {
    if (window.confirm(`Add ${ipAddress} to blacklist?`)) {
      blacklistMutation.mutate(ipAddress);
    }
  }, [blacklistMutation]);

  const handleRowClick = useCallback((params: GridRowParams<EnrichedEvent>) => {
    setSelectedEvent(params.row);
    setDrawerOpen(true);
    setMetadataExpanded(false);
  }, []);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    const cleared = { sourceIp: '', countryCode: '', protocol: '', asn: '', startDate: '', endDate: '' };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setQuickSearch('');
    setTimePreset('24h');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleQuickSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // ============================================================
  // DataGrid Columns
  // ============================================================

  const columns: GridColDef<EnrichedEvent>[] = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: 'Time',
        minWidth: 160,
        flex: 1,
        renderCell: (params) => {
          const receivedAt = new Date(params.value);
          const eventTime = new Date(params.row.timestamp);
          return (
            <Tooltip title={`Received: ${format(receivedAt, 'yyyy-MM-dd HH:mm:ss')}\nEvent time: ${format(eventTime, 'yyyy-MM-dd HH:mm:ss')}`} placement="right">
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem', lineHeight: 1.4 }}>
                  {format(receivedAt, 'MMM dd, HH:mm:ss')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                  {formatDistanceToNow(receivedAt, { addSuffix: true })}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'sourceIp',
        headerName: 'Source',
        minWidth: 170,
        flex: 1.2,
        renderCell: (params) => (
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ fontFamily: '"JetBrains Mono", "Fira Code", monospace', fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.4 }}>
              {params.value}
            </Typography>
            {params.row.sourceHostname && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {params.row.sourceHostname}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'sourceCountryCode',
        headerName: 'Origin',
        minWidth: 130,
        flex: 1,
        renderCell: (params) => {
          if (!params.value) return <Typography variant="body2" color="text.secondary">-</Typography>;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, overflow: 'hidden' }}>
              <Typography sx={{ fontSize: '1.1rem', lineHeight: 1, flexShrink: 0 }}>{countryFlag(params.value)}</Typography>
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {params.row.sourceCountryName || params.value}
                </Typography>
                {params.row.sourceCity && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {params.row.sourceCity}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'sourceAsn',
        headerName: 'Network',
        minWidth: 150,
        flex: 1.2,
        renderCell: (params) => {
          if (!params.value) return <Typography variant="body2" color="text.secondary">-</Typography>;
          return (
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem', lineHeight: 1.4 }}>
                AS{params.value}
              </Typography>
              {params.row.sourceAsnOrg && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {params.row.sourceAsnOrg}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: 'destinationPort',
        headerName: 'Target',
        minWidth: 130,
        flex: 0.9,
        renderCell: (params) => (
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ fontFamily: '"JetBrains Mono", "Fira Code", monospace', fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.4 }}>
              :{params.value || '?'}
            </Typography>
            {params.row.destinationIp && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: '"JetBrains Mono", "Fira Code", monospace', fontSize: '0.6875rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {params.row.destinationIp}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'protocol',
        headerName: 'Proto',
        minWidth: 80,
        flex: 0.5,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            label={params.value?.toUpperCase() || '?'}
            size="small"
            color={getProtocolColor(params.value)}
            variant="outlined"
            sx={{ fontWeight: 700, fontSize: '0.6875rem', height: 24, minWidth: 48 }}
          />
        ),
      },
      {
        field: 'action',
        headerName: 'Action',
        minWidth: 105,
        flex: 0.6,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            icon={getActionIcon(params.value) || undefined}
            label={params.value?.toUpperCase() || '?'}
            size="small"
            color={getActionColor(params.value)}
            sx={{ fontWeight: 700, fontSize: '0.6875rem', height: 24 }}
          />
        ),
      },
      {
        field: 'eventSource',
        headerName: 'Type',
        minWidth: 95,
        flex: 0.6,
        renderCell: (params) => (
          <Chip
            label={params.value || '?'}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.6875rem', height: 24, textTransform: 'capitalize' }}
          />
        ),
      },
      {
        field: 'sourceIdentifier',
        headerName: 'Identifier',
        minWidth: 120,
        flex: 0.8,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              fontSize: '0.75rem',
              fontWeight: 500,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.value || '-'}
          </Typography>
        ),
      },
    ],
    [],
  );

  // ============================================================
  // Render
  // ============================================================

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ====== Header ====== */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Events
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.totalCount !== undefined
              ? `${data.totalCount.toLocaleString()} events found`
              : 'Loading events...'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} disabled={isFetching} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
              <RefreshIcon sx={{ fontSize: 20, animation: isFetching ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* ====== Toolbar: Search + Time presets + Filter toggle ====== */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {/* Quick IP search */}
        <Box component="form" onSubmit={handleQuickSearchSubmit} sx={{ flex: '1 1 260px', maxWidth: 340 }}>
          <TextField
            placeholder="Search by IP address..."
            size="small"
            fullWidth
            value={quickSearch}
            onChange={(e) => setQuickSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: quickSearch ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setQuickSearch(''); setPaginationModel((p) => ({ ...p, page: 0 })); }}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, height: 38, fontSize: '0.875rem' } }}
          />
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Time range presets */}
        <ToggleButtonGroup
          exclusive
          value={timePreset}
          onChange={(_, val) => { if (val) { setTimePreset(val); setPaginationModel((p) => ({ ...p, page: 0 })); } }}
          size="small"
          sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: '0.75rem', fontWeight: 600, borderRadius: '8px !important', border: '1px solid', borderColor: 'divider', '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', borderColor: alpha(theme.palette.primary.main, 0.3) } } }}
        >
          {timePresets.map((tp) => (
            <ToggleButton key={tp.value} value={tp.value}>
              {tp.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ flex: 1 }} />

        {/* Advanced filters toggle */}
        <Badge badgeContent={activeFilterCount} color="primary" overlap="rectangular">
          <Button
            variant={filtersOpen ? 'contained' : 'outlined'}
            size="small"
            startIcon={<TuneIcon />}
            onClick={() => setFiltersOpen((prev) => !prev)}
            sx={{ height: 38, textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
          >
            Filters
          </Button>
        </Badge>

        {activeFilterCount > 0 && (
          <Button variant="text" size="small" onClick={handleClearFilters} startIcon={<ClearIcon />} sx={{ height: 38, textTransform: 'none', fontSize: '0.8125rem', color: 'text.secondary' }}>
            Clear all
          </Button>
        )}
      </Paper>

      {/* ====== Advanced Filters Collapsible ====== */}
      <Collapse in={filtersOpen}>
        <Paper sx={{ p: 2.5, mb: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>
            Advanced Filters
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Source IP"
              size="small"
              value={filters.sourceIp}
              onChange={(e) => setFilters((f) => ({ ...f, sourceIp: e.target.value }))}
              placeholder="e.g. 192.168.1.100"
            />
            <TextField
              label="Country Code"
              size="small"
              value={filters.countryCode}
              onChange={(e) => setFilters((f) => ({ ...f, countryCode: e.target.value }))}
              placeholder="e.g. US, DE, CN"
            />
            <FormControl size="small">
              <InputLabel>Protocol</InputLabel>
              <Select
                value={filters.protocol}
                label="Protocol"
                onChange={(e) => setFilters((f) => ({ ...f, protocol: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="tcp">TCP</MenuItem>
                <MenuItem value="udp">UDP</MenuItem>
                <MenuItem value="icmp">ICMP</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="ASN"
              size="small"
              value={filters.asn}
              onChange={(e) => setFilters((f) => ({ ...f, asn: e.target.value }))}
              placeholder="e.g. 13335"
            />
            <TextField
              label="Start Date"
              type="datetime-local"
              size="small"
              value={filters.startDate}
              onChange={(e) => { setFilters((f) => ({ ...f, startDate: e.target.value })); setTimePreset('custom'); }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="datetime-local"
              size="small"
              value={filters.endDate}
              onChange={(e) => { setFilters((f) => ({ ...f, endDate: e.target.value })); setTimePreset('custom'); }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button variant="contained" size="small" onClick={handleApplyFilters} sx={{ textTransform: 'none' }}>
              Apply Filters
            </Button>
            <Button variant="text" size="small" onClick={() => setFiltersOpen(false)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
              Cancel
            </Button>
          </Stack>
        </Paper>
      </Collapse>

      {/* ====== Active filter chips ====== */}
      {activeFilterCount > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
          {appliedFilters.sourceIp && (
            <Chip label={`IP: ${appliedFilters.sourceIp}`} size="small" onDelete={() => setAppliedFilters((f) => ({ ...f, sourceIp: '' }))} variant="outlined" color="primary" />
          )}
          {appliedFilters.countryCode && (
            <Chip label={`Country: ${appliedFilters.countryCode}`} size="small" onDelete={() => setAppliedFilters((f) => ({ ...f, countryCode: '' }))} variant="outlined" color="primary" />
          )}
          {appliedFilters.protocol && (
            <Chip label={`Protocol: ${appliedFilters.protocol.toUpperCase()}`} size="small" onDelete={() => setAppliedFilters((f) => ({ ...f, protocol: '' }))} variant="outlined" color="primary" />
          )}
          {appliedFilters.asn && (
            <Chip label={`ASN: ${appliedFilters.asn}`} size="small" onDelete={() => setAppliedFilters((f) => ({ ...f, asn: '' }))} variant="outlined" color="primary" />
          )}
        </Stack>
      )}

      {/* ====== Events DataGrid ====== */}
      <Paper sx={{ flex: 1, minHeight: 500, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <DataGrid
          rows={data?.data || []}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={data?.totalCount || 0}
          paginationMode="server"
          loading={isLoading || isFetching}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          getRowHeight={() => 'auto'}
          getRowClassName={(params) => {
            const action = params.row.action?.toLowerCase();
            if (action === 'denied' || action === 'drop' || action === 'blocked') return 'row-denied';
            return '';
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: '#f8fafc',
              borderBottom: '2px solid',
              borderColor: 'divider',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'text.secondary',
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              transition: 'background-color 0.15s',
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
            },
            '& .MuiDataGrid-row.row-denied': {
              bgcolor: alpha(theme.palette.error.main, 0.03),
              '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.07) },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid',
              borderColor: alpha(theme.palette.divider, 0.5),
              py: 1.5,
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '2px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Paper>

      {/* ====== Event Detail Drawer ====== */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480 },
            borderRadius: 0,
            boxShadow: '-8px 0 30px rgba(0,0,0,0.1)',
          },
        }}
      >
        {selectedEvent && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Drawer header */}
            <Box sx={{ p: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', gap: 1.5, bgcolor: '#f8fafc' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 0.5 }}>
                  Event #{selectedEvent.id}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  <Chip
                    icon={getActionIcon(selectedEvent.action) || undefined}
                    label={selectedEvent.action?.toUpperCase()}
                    size="small"
                    color={getActionColor(selectedEvent.action)}
                    sx={{ fontWeight: 700, fontSize: '0.6875rem' }}
                  />
                  <Chip label={selectedEvent.protocol?.toUpperCase() || '?'} size="small" color={getProtocolColor(selectedEvent.protocol)} variant="outlined" sx={{ fontWeight: 700, fontSize: '0.6875rem' }} />
                  <Chip label={selectedEvent.eventSource} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'capitalize' }} />
                </Box>
              </Box>
              <IconButton onClick={() => setDrawerOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Drawer body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, pb: 3 }}>
              {/* Timing */}
              <SectionHeader title="Timing" icon={<AccessTimeIcon sx={{ fontSize: 18 }} />} />
              <DetailRow icon={<AccessTimeIcon sx={{ fontSize: 18 }} />} label="Event Time" value={format(new Date(selectedEvent.timestamp), 'yyyy-MM-dd HH:mm:ss.SSS')} />
              <DetailRow icon={<AccessTimeIcon sx={{ fontSize: 18 }} />} label="Processed At" value={selectedEvent.createdAt ? format(new Date(selectedEvent.createdAt), 'yyyy-MM-dd HH:mm:ss') : '-'} />
              <DetailRow icon={<FingerprintIcon sx={{ fontSize: 18 }} />} label="Event ID / Raw Event ID" value={`#${selectedEvent.id}${selectedEvent.rawEventId ? ` / #${selectedEvent.rawEventId}` : ''}`} />

              {/* Source */}
              <SectionHeader title="Source" icon={<LanIcon sx={{ fontSize: 18 }} />} />
              <DetailRow icon={<LanIcon sx={{ fontSize: 18 }} />} label="IP Address" value={selectedEvent.sourceIp} mono copyable={selectedEvent.sourceIp} />
              <DetailRow icon={<DnsIcon sx={{ fontSize: 18 }} />} label="Hostname" value={selectedEvent.sourceHostname} mono copyable={selectedEvent.sourceHostname || undefined} />
              <DetailRow icon={<RouterIcon sx={{ fontSize: 18 }} />} label="BGP Prefix" value={selectedEvent.sourceBgpPrefix} mono copyable={selectedEvent.sourceBgpPrefix || undefined} />
              <DetailRow
                icon={<PublicIcon sx={{ fontSize: 18 }} />}
                label="ASN"
                value={
                  selectedEvent.sourceAsn ? (
                    <Box>
                      <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>AS{selectedEvent.sourceAsn}</Typography>
                      {selectedEvent.sourceAsnOrg && (
                        <Typography component="span" variant="body2" color="text.secondary"> - {selectedEvent.sourceAsnOrg}</Typography>
                      )}
                    </Box>
                  ) : null
                }
              />

              {/* Location */}
              <SectionHeader title="Geolocation" icon={<LocationOnIcon sx={{ fontSize: 18 }} />} />
              <DetailRow
                icon={<PublicIcon sx={{ fontSize: 18 }} />}
                label="Country"
                value={
                  selectedEvent.sourceCountryCode ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography sx={{ fontSize: '1.1rem' }}>{countryFlag(selectedEvent.sourceCountryCode)}</Typography>
                      <Typography variant="body2">
                        {selectedEvent.sourceCountryName || selectedEvent.sourceCountryCode}
                        {selectedEvent.sourceCountryCode && ` (${selectedEvent.sourceCountryCode})`}
                      </Typography>
                    </Box>
                  ) : null
                }
              />
              <DetailRow icon={<LocationOnIcon sx={{ fontSize: 18 }} />} label="City" value={selectedEvent.sourceCity} />
              {selectedEvent.sourceLatitude != null && selectedEvent.sourceLongitude != null && (
                <DetailRow
                  icon={<LocationOnIcon sx={{ fontSize: 18 }} />}
                  label="Coordinates"
                  value={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {selectedEvent.sourceLatitude.toFixed(4)}, {selectedEvent.sourceLongitude.toFixed(4)}
                      </Typography>
                      <Tooltip title="Open in Google Maps">
                        <IconButton
                          size="small"
                          onClick={() => window.open(`https://maps.google.com/maps?q=${selectedEvent.sourceLatitude},${selectedEvent.sourceLongitude}`, '_blank')}
                          sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
              )}

              {/* Target */}
              <SectionHeader title="Target" icon={<StorageIcon sx={{ fontSize: 18 }} />} />
              <DetailRow icon={<StorageIcon sx={{ fontSize: 18 }} />} label="Destination IP" value={selectedEvent.destinationIp} mono copyable={selectedEvent.destinationIp || undefined} />
              <DetailRow icon={<RouterIcon sx={{ fontSize: 18 }} />} label="Destination Port" value={selectedEvent.destinationPort?.toString()} mono />
              <DetailRow icon={<LanIcon sx={{ fontSize: 18 }} />} label="Protocol" value={selectedEvent.protocol?.toUpperCase()} />
              <DetailRow icon={<WarningAmberIcon sx={{ fontSize: 18 }} />} label="Action" value={selectedEvent.action?.toUpperCase()} />

              {/* Context */}
              <SectionHeader title="Context" icon={<SensorsIcon sx={{ fontSize: 18 }} />} />
              <DetailRow icon={<SensorsIcon sx={{ fontSize: 18 }} />} label="Event Source" value={selectedEvent.eventSource} />
              <DetailRow icon={<FingerprintIcon sx={{ fontSize: 18 }} />} label="Source Identifier" value={selectedEvent.sourceIdentifier} />

              {/* Metadata */}
              {selectedEvent.metadata && Object.keys(selectedEvent.metadata).length > 0 && (
                <>
                  <SectionHeader title="Metadata" icon={<DataObjectIcon sx={{ fontSize: 18 }} />} />
                  <Box
                    onClick={() => setMetadataExpanded((prev) => !prev)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      py: 1,
                      px: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      border: '1px solid',
                      borderColor: alpha(theme.palette.divider, 0.5),
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                      {Object.keys(selectedEvent.metadata).length} field(s)
                    </Typography>
                    {metadataExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </Box>
                  <Collapse in={metadataExpanded}>
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: '#1e293b',
                        overflow: 'auto',
                        maxHeight: 400,
                      }}
                    >
                      <pre
                        style={{
                          margin: 0,
                          color: '#e2e8f0',
                          fontSize: '0.75rem',
                          fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {JSON.stringify(selectedEvent.metadata, null, 2)}
                      </pre>
                    </Box>
                  </Collapse>
                </>
              )}
            </Box>

            {/* Drawer footer */}
            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="error"
                fullWidth
                startIcon={<BlockIcon />}
                onClick={() => {
                  handleBlacklistIp(selectedEvent.sourceIp);
                  setDrawerOpen(false);
                }}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Blacklist IP
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setQuickSearch(selectedEvent.sourceIp);
                  setDrawerOpen(false);
                  setPaginationModel((p) => ({ ...p, page: 0 }));
                }}
                startIcon={<SearchIcon />}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Filter by IP
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};
